import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import {
    Home, User, Users, BookOpen, LogOut, LayoutDashboard,
    Building2, Bell, Search, Plus, Menu, X, ChevronDown,
    Settings, Sparkles
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

const SidebarItem = ({ to, icon: Icon, label, badge, onClick }) => (
    <NavLink
        to={to}
        onClick={onClick}
        className={({ isActive }) =>
            `sf-nav-item ${isActive ? 'active' : ''}`
        }
    >
        <Icon size={18} strokeWidth={2} />
        <span className="flex-1">{label}</span>
        {badge && (
            <span className="sf-badge sf-badge-primary" style={{ fontSize: '10px', padding: '2px 6px' }}>
                {badge}
            </span>
        )}
    </NavLink>
);

const AppLayout = ({ children }) => {
    const { userRole, logout, currentUser } = useAuth();
    const [showNotifications, setShowNotifications] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [searchFocused, setSearchFocused] = useState(false);

    const handleLogout = async () => {
        await logout();
    };

    const closeMobileMenu = () => setMobileMenuOpen(false);

    const notifications = [
        { id: 1, text: 'Sarah Jenkins liked your post', time: '5m ago', unread: true },
        { id: 2, text: 'New announcement in Physics class', time: '1h ago', unread: true },
        { id: 3, text: 'You were added to JEE Prep group', time: '2h ago', unread: false },
    ];

    const unreadCount = notifications.filter(n => n.unread).length;
    const displayName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User';
    const roleLabel = (userRole || 'student').charAt(0).toUpperCase() + (userRole || 'student').slice(1);

    const renderNavItems = (onClick = () => { }) => (
        <>
            <div style={{ padding: '8px 16px', fontSize: '11px', fontWeight: '700', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Main Menu
            </div>
            <SidebarItem to="/" icon={Home} label="Home Feed" onClick={onClick} />
            <SidebarItem to="/groups" icon={Users} label="Groups" onClick={onClick} />
            <SidebarItem to="/classes" icon={BookOpen} label="Classes" badge={userRole === 'teacher' ? '3' : null} onClick={onClick} />

            <div style={{ padding: '16px 16px 8px', fontSize: '11px', fontWeight: '700', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Dashboard
            </div>
            {userRole === 'school' ? (
                <SidebarItem to="/school-dashboard" icon={Building2} label="School Dashboard" onClick={onClick} />
            ) : (
                <SidebarItem to="/dashboard" icon={LayoutDashboard} label="My Dashboard" onClick={onClick} />
            )}
            <SidebarItem to="/profile" icon={User} label="My Profile" onClick={onClick} />

            {userRole === 'school' && (
                <>
                    <div style={{ padding: '16px 16px 8px', fontSize: '11px', fontWeight: '700', color: '#706e6b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Management
                    </div>
                    <a
                        href="/erp"
                        onClick={(e) => {
                            e.preventDefault();
                            if (onClick) onClick();
                            window.location.href = '/erp'; // Force full navigation or use navigate
                        }}
                        className="sf-nav-item"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            color: 'rgba(255,255,255,0.8)',
                            textDecoration: 'none',
                            borderRadius: '8px',
                            marginBottom: '2px',
                            cursor: 'pointer'
                        }}
                    >
                        <Building2 size={18} strokeWidth={2} />
                        <span className="flex-1">Switch to ERP</span>
                    </a>
                </>
            )}
        </>
    );

    return (
        <div style={{
            display: 'flex',
            minHeight: '100vh',
            background: 'var(--slds-bg-page)'
        }}>
            {/* Premium Salesforce Sidebar - Desktop */}
            <aside style={{
                width: '240px',
                background: 'linear-gradient(180deg, #032d60 0%, #014486 50%, #0176d3 100%)',
                display: 'none',
                flexDirection: 'column',
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 100,
                boxShadow: '4px 0 12px rgba(0,0,0,0.15)',
            }} className="md:!flex">
                {/* Logo */}
                <div style={{
                    padding: '20px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}>
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
                        <img src={logo} alt="Fiscoe" style={{ width: '40px', height: '40px' }} />
                        <div>
                            <h1 style={{
                                fontSize: '20px',
                                fontWeight: '700',
                                color: '#fff',
                                margin: 0,
                                letterSpacing: '-0.5px'
                            }}>Fiscoe</h1>
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontSize: '11px',
                                color: '#7ecbff',
                                fontWeight: '600',
                            }}>
                                <Sparkles size={10} />
                                CORE MVP
                            </span>
                        </div>
                    </Link>
                </div>

                {/* Navigation */}
                <nav style={{
                    flex: 1,
                    padding: '16px 12px',
                    overflowY: 'auto',
                }}>
                    <style>{`
                        .sidebar-nav .sf-nav-item {
                            color: rgba(255,255,255,0.8) !important;
                            background: transparent !important;
                            border-radius: 8px;
                            margin-bottom: 2px;
                        }
                        .sidebar-nav .sf-nav-item:hover {
                            background: rgba(255,255,255,0.1) !important;
                            color: #fff !important;
                        }
                        .sidebar-nav .sf-nav-item.active {
                            background: rgba(255,255,255,0.2) !important;
                            color: #fff !important;
                            font-weight: 600;
                        }
                        .sidebar-nav .sf-nav-item.active::before {
                            background: #45c65a;
                        }
                    `}</style>
                    <div className="sidebar-nav">
                        {renderNavItems()}
                    </div>
                </nav>

                {/* User Section */}
                <div style={{
                    padding: '16px',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(0,0,0,0.1)',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        borderRadius: '8px',
                        background: 'rgba(255,255,255,0.1)',
                        marginBottom: '12px',
                    }}>
                        <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <img
                                src={currentUser?.photoURL || logo}
                                alt="Profile"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{
                                fontSize: '13px',
                                fontWeight: '600',
                                color: '#fff',
                                margin: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {displayName}
                            </p>
                            <span style={{ fontSize: '11px', color: '#7ecbff' }}>
                                {roleLabel}
                            </span>
                        </div>
                    </div>

                    <button
                        onClick={handleLogout}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: '10px 12px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: 'rgba(255,255,255,0.7)',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = 'rgba(255,100,100,0.2)';
                            e.currentTarget.style.color = '#ff8080';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                        }}
                    >
                        <LogOut size={16} />
                        Sign Out
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main style={{
                flex: 1,
                marginLeft: '0',
                paddingBottom: '80px',
            }} className="md:!ml-60">
                {/* Top Header Bar */}
                <header style={{
                    background: '#fff',
                    borderBottom: '1px solid var(--slds-border-light)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 50,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 24px',
                        maxWidth: '1400px',
                        margin: '0 auto',
                    }}>
                        {/* Mobile Menu */}
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                padding: '8px',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#3e3e3c',
                            }}
                            className="md:!hidden !flex"
                        >
                            <Menu size={24} />
                        </button>

                        {/* Search Bar */}
                        <div style={{
                            flex: 1,
                            maxWidth: '480px',
                            position: 'relative',
                        }}>
                            <Search
                                size={18}
                                style={{
                                    position: 'absolute',
                                    left: '14px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: searchFocused ? 'var(--slds-brand-primary)' : '#706e6b',
                                    transition: 'color 0.15s ease',
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Search posts, groups, classes..."
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                                style={{
                                    width: '100%',
                                    padding: '10px 14px 10px 44px',
                                    fontSize: '14px',
                                    border: searchFocused ? '2px solid var(--slds-brand-primary)' : '2px solid var(--slds-border-light)',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    background: searchFocused ? '#fff' : 'var(--slds-bg-surface-secondary)',
                                    transition: 'all 0.15s ease',
                                }}
                            />
                        </div>

                        {/* Right Actions */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {/* Switch to ERP */}
                            {userRole === 'school' && (
                                <Link
                                    to="/erp"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '8px 16px',
                                        background: '#fff',
                                        border: '1px solid var(--slds-border-light)',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        color: 'var(--slds-brand-primary)',
                                        textDecoration: 'none',
                                        marginRight: '8px',
                                    }}
                                >
                                    <Building2 size={18} />
                                    <span className="sm:inline hidden">Switch to ERP</span>
                                </Link>
                            )}

                            {/* Create Button */}
                            <Link
                                to="/create"
                                className="sf-button sf-button-primary sm:!flex !hidden"
                            >
                                <Plus size={18} />
                                Create
                            </Link>

                            {/* Notifications */}
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowNotifications(!showNotifications)}
                                    style={{
                                        padding: '10px',
                                        background: showNotifications ? 'var(--slds-brand-primary-lighter)' : 'transparent',
                                        border: 'none',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        color: showNotifications ? 'var(--slds-brand-primary)' : '#706e6b',
                                        position: 'relative',
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <Bell size={20} />
                                    {unreadCount > 0 && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            width: '18px',
                                            height: '18px',
                                            background: 'linear-gradient(135deg, #ff5d5d 0%, #c23934 100%)',
                                            borderRadius: '50%',
                                            color: '#fff',
                                            fontSize: '10px',
                                            fontWeight: '700',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 4px rgba(194,57,52,0.4)',
                                        }}>
                                            {unreadCount}
                                        </span>
                                    )}
                                </button>

                                {showNotifications && (
                                    <div style={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 'calc(100% + 8px)',
                                        width: '360px',
                                        background: '#fff',
                                        borderRadius: '12px',
                                        boxShadow: 'var(--slds-shadow-dropdown)',
                                        border: '1px solid var(--slds-border-light)',
                                        overflow: 'hidden',
                                        animation: 'slideUp 0.15s ease',
                                    }}>
                                        <div style={{
                                            padding: '16px',
                                            borderBottom: '1px solid var(--slds-border-light)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}>
                                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '700' }}>Notifications</h4>
                                            <span className="sf-badge sf-badge-primary">{unreadCount} new</span>
                                        </div>
                                        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                                            {notifications.map(notif => (
                                                <div
                                                    key={notif.id}
                                                    style={{
                                                        padding: '14px 16px',
                                                        borderBottom: '1px solid var(--slds-border-light)',
                                                        cursor: 'pointer',
                                                        background: notif.unread ? 'var(--slds-info-bg)' : '#fff',
                                                        transition: 'background 0.1s ease',
                                                    }}
                                                    onMouseOver={(e) => e.currentTarget.style.background = 'var(--slds-bg-surface-secondary)'}
                                                    onMouseOut={(e) => e.currentTarget.style.background = notif.unread ? 'var(--slds-info-bg)' : '#fff'}
                                                >
                                                    <p style={{ margin: 0, fontSize: '13px', color: '#181818' }}>{notif.text}</p>
                                                    <span style={{ fontSize: '11px', color: '#706e6b', marginTop: '4px', display: 'block' }}>{notif.time}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* User Menu */}
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        padding: '6px 12px 6px 6px',
                                        background: showUserMenu ? 'var(--slds-bg-surface-secondary)' : 'transparent',
                                        border: '1px solid transparent',
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = 'var(--slds-bg-surface-secondary)'}
                                    onMouseOut={(e) => e.currentTarget.style.background = showUserMenu ? 'var(--slds-bg-surface-secondary)' : 'transparent'}
                                >
                                    <div style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '8px',
                                        background: 'linear-gradient(135deg, var(--slds-brand-primary) 0%, #5a9fd4 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontWeight: '700',
                                        fontSize: '13px',
                                    }}>
                                        {displayName[0]?.toUpperCase()}
                                    </div>
                                    <ChevronDown size={14} style={{ color: '#706e6b' }} />
                                </button>

                                {showUserMenu && (
                                    <div style={{
                                        position: 'absolute',
                                        right: 0,
                                        top: 'calc(100% + 8px)',
                                        width: '240px',
                                        background: '#fff',
                                        borderRadius: '12px',
                                        boxShadow: 'var(--slds-shadow-dropdown)',
                                        border: '1px solid var(--slds-border-light)',
                                        overflow: 'hidden',
                                        animation: 'slideUp 0.15s ease',
                                    }}>
                                        <div style={{
                                            padding: '16px',
                                            borderBottom: '1px solid var(--slds-border-light)',
                                            background: 'var(--slds-bg-surface-secondary)',
                                        }}>
                                            <p style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>{displayName}</p>
                                            <span style={{ fontSize: '12px', color: '#706e6b' }}>{currentUser?.email}</span>
                                        </div>
                                        <div style={{ padding: '8px' }}>
                                            <Link
                                                to="/profile"
                                                onClick={() => setShowUserMenu(false)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    padding: '10px 12px',
                                                    borderRadius: '6px',
                                                    color: '#3e3e3c',
                                                    textDecoration: 'none',
                                                    fontSize: '13px',
                                                    transition: 'background 0.1s ease',
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.background = 'var(--slds-bg-surface-secondary)'}
                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <User size={16} /> My Profile
                                            </Link>
                                            <Link
                                                to="/settings"
                                                onClick={() => setShowUserMenu(false)}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    padding: '10px 12px',
                                                    borderRadius: '6px',
                                                    color: '#3e3e3c',
                                                    textDecoration: 'none',
                                                    fontSize: '13px',
                                                    transition: 'background 0.1s ease',
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.background = 'var(--slds-bg-surface-secondary)'}
                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <Settings size={16} /> Settings
                                            </Link>
                                        </div>
                                        <div style={{ padding: '8px', borderTop: '1px solid var(--slds-border-light)' }}>
                                            <button
                                                onClick={handleLogout}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    borderRadius: '6px',
                                                    color: '#c23934',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    transition: 'background 0.1s ease',
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.background = 'var(--slds-error-bg)'}
                                                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                                            >
                                                <LogOut size={16} /> Sign Out
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '24px',
                }}>
                    {children}
                </div>
            </main>

            {/* Mobile Drawer */}
            {mobileMenuOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0,0,0,0.5)',
                            backdropFilter: 'blur(4px)',
                        }}
                        onClick={closeMobileMenu}
                    />
                    <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '280px',
                        background: 'linear-gradient(180deg, #032d60 0%, #014486 100%)',
                        boxShadow: '4px 0 24px rgba(0,0,0,0.3)',
                        display: 'flex',
                        flexDirection: 'column',
                        animation: 'slideInLeft 0.2s ease',
                    }}>
                        <style>{`
                            @keyframes slideInLeft {
                                from { transform: translateX(-100%); }
                                to { transform: translateX(0); }
                            }
                        `}</style>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '16px 20px',
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #1b96ff 0%, #45c65a 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontWeight: '800',
                                    fontSize: '18px',
                                }}>
                                    F
                                </div>
                                <span style={{ fontSize: '18px', fontWeight: '700', color: '#fff' }}>Fiscoe</span>
                            </div>
                            <button
                                onClick={closeMobileMenu}
                                style={{
                                    padding: '8px',
                                    background: 'rgba(255,255,255,0.1)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    color: '#fff',
                                }}
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <nav style={{ flex: 1, padding: '16px 12px' }} className="sidebar-nav">
                            {renderNavItems(closeMobileMenu)}
                        </nav>
                        <div style={{
                            padding: '16px',
                            borderTop: '1px solid rgba(255,255,255,0.1)',
                        }}>
                            <button
                                onClick={handleLogout}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    width: '100%',
                                    padding: '12px',
                                    background: 'rgba(255,100,100,0.2)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#ff8080',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                }}
                            >
                                <LogOut size={18} />
                                Sign Out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile Bottom Nav */}
            <div style={{
                display: 'flex',
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: '#fff',
                borderTop: '1px solid var(--slds-border-light)',
                zIndex: 50,
                padding: '8px 0',
                boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
            }} className="md:!hidden">
                <NavLink
                    to="/"
                    style={({ isActive }) => ({
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px',
                        color: isActive ? 'var(--slds-brand-primary)' : '#706e6b',
                        textDecoration: 'none',
                        fontSize: '11px',
                        fontWeight: '600',
                    })}
                >
                    <Home size={22} />
                    Home
                </NavLink>
                <NavLink
                    to="/groups"
                    style={({ isActive }) => ({
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px',
                        color: isActive ? 'var(--slds-brand-primary)' : '#706e6b',
                        textDecoration: 'none',
                        fontSize: '11px',
                        fontWeight: '600',
                    })}
                >
                    <Users size={22} />
                    Groups
                </NavLink>
                <Link
                    to="/create"
                    style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1b96ff 0%, #0176d3 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '-28px',
                        boxShadow: '0 4px 12px rgba(1,118,211,0.4)',
                        border: '4px solid #fff',
                    }}
                >
                    <Plus size={24} style={{ color: '#fff' }} />
                </Link>
                <NavLink
                    to="/classes"
                    style={({ isActive }) => ({
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px',
                        color: isActive ? 'var(--slds-brand-primary)' : '#706e6b',
                        textDecoration: 'none',
                        fontSize: '11px',
                        fontWeight: '600',
                    })}
                >
                    <BookOpen size={22} />
                    Classes
                </NavLink>
                <NavLink
                    to="/profile"
                    style={({ isActive }) => ({
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px',
                        color: isActive ? 'var(--slds-brand-primary)' : '#706e6b',
                        textDecoration: 'none',
                        fontSize: '11px',
                        fontWeight: '600',
                    })}
                >
                    <User size={22} />
                    Profile
                </NavLink>
            </div>

            <style>{`
                @media (min-width: 768px) {
                    .md\\:!flex { display: flex !important; }
                    .md\\:!hidden { display: none !important; }
                    .md\\:!ml-60 { margin-left: 240px !important; }
                }
                @media (min-width: 640px) {
                    .sm\\:!flex { display: flex !important; }
                }
                .!hidden { display: none !important; }
                .!flex { display: flex !important; }
            `}</style>
        </div>
    );
};

export default AppLayout;
